import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  CircularProgress,
  Pagination,
  Typography,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useTheme, useMediaQuery } from "@mui/material";

const PrescriptionTable = ({
  loading,
  prescriptions,
  handleEditData,
  handleDeleteData,
}) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchField, setSearchField] = useState("customer_name");

  const handleSearchFieldChange = (event) => {
    setSearchField(event.target.value);
  };

  const filteredData = prescriptions.filter((row) =>
    row[searchField]?.toLowerCase().includes(search.toLowerCase())
  );

  // Paginate the filtered data
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Paper elevation={3} sx={{ padding: 2, borderRadius: 3, width: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Typography variant="subtitle2">
          Total: {filteredData.length}
        </Typography>

        <div style={{ display: "flex", gap: "10px" }}>
          <FormControl variant="outlined" size="small">
            <InputLabel>Search By</InputLabel>
            <Select
              value={searchField}
              onChange={handleSearchFieldChange}
              label="Search By"
            >
              <MenuItem value="customer_name">Customer Name</MenuItem>
              <MenuItem value="prescribed_by">Prescribed By</MenuItem>
              <MenuItem value="item_name">Pain Areas</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {isSmallScreen ? null : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Customer Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Pain Areas</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Prescribed By</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Prescribed At</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          )}
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <CircularProgress />
          </div>
        </motion.div>
      ) : filteredData.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{ textAlign: "center", padding: "40px 0", fontWeight: "bold" }}
        >
          No Data Found
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {isSmallScreen ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {paginatedData.map((row, index) => (
                <Paper
                  key={row.id}
                  elevation={2}
                  sx={{
                    padding: 2,
                    backgroundColor: row.is_admin === 1 ? "#d0f0c0" : "#fff",
                    borderRadius: 2,
                  }}
                >
                  <Typography fontWeight="bold" gutterBottom>
                    {row.customer_name || "N/A"}
                  </Typography>
                  <Typography>Product: {row.item_name || "N/A"}</Typography>
                  <Typography>
                    Prescribed By: {row.prescribed_by || "N/A"}
                  </Typography>
                  <Typography>
                    Prescribed At: {row.prescribed_at || "N/A"}
                  </Typography>
                  <Box mt={2} display="flex" gap={1}>
                    <IconButton
                      color="secondary"
                      onClick={() => handleEditData(row)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteData(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No.</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Customer Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Pain Areas</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Prescribed By</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Prescribed At</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow
                      key={row.id}
                      sx={
                        row.is_admin === 1 ? { backgroundColor: "#d0f0c0" } : {}
                      }
                    >
                      <TableCell>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{row.customer_name || "N/A"}</TableCell>
                      <TableCell>{row.item_name || "N/A"}</TableCell>
                      <TableCell>{row.prescribed_by || "N/A"}</TableCell>
                      <TableCell>
                        {row.prescribed_at
                          ? new Date(row.prescribed_at).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )
                          : "N/A"}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          color="secondary"
                          //   onClick={() => handleEditData(row)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          //   onClick={() => handleDeleteData(row)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 16,
            }}
          >
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </div>
        </motion.div>
      )}
    </Paper>
  );
};

export default PrescriptionTable;
